import { useContext, useEffect, useMemo } from 'react';
import type { Betslip } from '@gin-betting/betslip';
import betslip from '@gin-betting/betslip';
import core, { GQL, View } from '@gin-betting/core';
import restrictions from '@gin-betting/restrictions';
import { createSelector, createStructuredSelector } from 'reselect';

import { usePureSelector } from 'hooks';
import type { State } from 'types';
import { RestrictionsContext } from '../../restrictions';

const {
    entities: {
        selectors: { selectOdds },
    },
    hooks: { useDeepMemo },
    appServiceLocator,
} = core;

const {
    selectors: { selectBetslipOddIdFromProps, selectBetslipOdd },
    utils: { getBetSystemSizes },
} = betslip;

const {
    actions: { getRestrictions },
    utils: { getRestrictionsSelection, checkIsCanGetRestrictions },
    constants: { GET_RESTRICTION_SUCCESS, GET_RESTRICTION_FAIL },
} = restrictions;

interface Input {
    betslipBetId: string;
    betslipOddId: string;
}

const useRestrictionsSingle = ({ betslipBetId, betslipOddId }: Input): void => {
    const { dispatchRestrictions, state: restrictionsState } = useContext(
        RestrictionsContext
    );

    const { selection, betslipOdd } = usePureSelector((s: State) =>
        selectProps(s, { betslipBetId, betslipOddId, restrictionsState })
    );

    const { singleStatus } = betslipOdd || ({} as Partial<Betslip.BetslipOdd>);

    const varsMemo = useDeepMemo(() => {
        if (!selection) return;

        return {
            selections: [selection],
            betType: GQL.BetType.SINGLE,
            systemSizes: getBetSystemSizes({
                betType: GQL.BetType.SINGLE,
            }),
        };
    }, [selection]);

    const argsMemo = useMemo(() => {
        return {
            betType: GQL.BetType.SINGLE,
            betslipBetId,
            betslipOddIds: [betslipOddId],
        };
    }, [betslipBetId, betslipOddId]);

    const isCanGetRestrictions = useMemo(() => {
        if (!singleStatus) return;

        return checkIsCanGetRestrictions(singleStatus);
    }, [singleStatus]);

    useEffect(() => {
        if (!isCanGetRestrictions || !varsMemo || !argsMemo) return;

        dispatchRestrictions(getRestrictions(varsMemo, argsMemo));

        appServiceLocator
            .get('apiClient')
            .makeRequest({
                method: 'getRestrictions',
                variables: { ...varsMemo, ...argsMemo },
            })
            .then(({ data }) => {
                dispatchRestrictions({
                    type: GET_RESTRICTION_SUCCESS,
                    payload: {
                        data: {
                            restrictions: data.restrictions as View.Restriction[],
                        },
                        ...argsMemo,
                    },
                });
            })
            .catch(error => {
                dispatchRestrictions({
                    type: GET_RESTRICTION_FAIL,
                    payload: {
                        args: argsMemo,
                        error,
                    },
                });
            });
    }, [
        varsMemo,
        argsMemo,
        isCanGetRestrictions,
        dispatchRestrictions,
        betslipOddId,
    ]);
};

export default useRestrictionsSingle;

const selectSelection = createSelector(
    selectOdds,
    selectBetslipOddIdFromProps,
    (odds, betslipOddId) => getRestrictionsSelection(betslipOddId, odds)
);

const selectProps = createStructuredSelector({
    betslipOdd: selectBetslipOdd,
    selection: selectSelection,
});
